import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { getIndustryBySlug } from '../data/industries/industryTrainingsData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowRight,
  CheckCircle,
  Clock,
  Award,
  Users,
  TrendingUp,
  MessageSquare,
  Brain,
  Target,
  HeartHandshake,
  Shield,
  Sparkles,
  Gamepad2,
  Zap,
  Heart,
  UserCheck,
  Presentation
} from 'lucide-react';
import { UpcomingTrainingsSection } from '../components/UpcomingTrainingsSection';
import { RelatedCoursesSection } from '../components/training/RelatedCoursesSection';

// Corporate Training Programs Data (Soft Skills & Development)
const corporateTrainingPrograms = [
  {
    id: 'business-english',
    title: 'Business English Training',
    icon: MessageSquare,
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-50',
    iconColor: 'text-blue-600',
    description: 'Essential for professionals working with global clients, multinational teams, and diverse stakeholders.',
    keyAreas: [
      'Professional speaking and presentation skills',
      'Business email writing and documentation',
      'Client meetings and negotiations',
      'Corporate vocabulary and tone',
      'Cross-cultural communication'
    ]
  },
  {
    id: 'behavioral-training',
    title: 'Behavioral Training',
    icon: UserCheck,
    color: 'from-purple-500 to-purple-600',
    bgColor: 'bg-purple-50',
    iconColor: 'text-purple-600',
    description: 'Focuses on developing positive workplace attitudes, emotional intelligence, and professional conduct.',
    keyAreas: [
      'Improved workplace etiquette and professionalism',
      'Stronger collaboration and interpersonal skills',
      'Accountability and ownership',
      'Adaptability to change'
    ]
  },
  {
    id: 'leadership-development',
    title: 'Leadership Development Programs',
    icon: Target,
    color: 'from-amber-500 to-orange-500',
    bgColor: 'bg-amber-50',
    iconColor: 'text-amber-600',
    description: 'Prepares managers and high-potential employees to lead teams, manage change, and deliver results.',
    keyAreas: [
      'Leadership styles and self-awareness',
      'Decision-making and problem-solving',
      'People management and coaching skills',
      'Strategic thinking and vision',
      'Influencing and communication'
    ]
  },
  {
    id: 'team-building-training',
    title: 'Team Building Training',
    icon: Users,
    color: 'from-green-500 to-emerald-500',
    bgColor: 'bg-green-50',
    iconColor: 'text-green-600',
    description: 'Helps employees work collaboratively toward shared goals, strengthening trust and cooperation.',
    keyAreas: [
      'Improved collaboration and coordination',
      'Stronger team relationships',
      'Enhanced problem-solving skills',
      'Higher morale and engagement'
    ]
  },
  {
    id: 'conflict-management',
    title: 'Conflict Management Training',
    icon: HeartHandshake,
    color: 'from-rose-500 to-pink-500',
    bgColor: 'bg-rose-50',
    iconColor: 'text-rose-600',
    description: 'Equips employees and leaders with tools to handle disagreements constructively.',
    keyAreas: [
      'Identifying sources of workplace conflict',
      'Effective communication during disagreements',
      'Negotiation and resolution techniques',
      'Emotional control and empathy'
    ]
  },
  {
    id: 'stress-management',
    title: 'Stress Management Training',
    icon: Shield,
    color: 'from-teal-500 to-cyan-500',
    bgColor: 'bg-teal-50',
    iconColor: 'text-teal-600',
    description: 'Helps employees cope effectively with pressure from high workloads and tight deadlines.',
    keyAreas: [
      'Understanding stress triggers',
      'Time and workload management',
      'Relaxation and coping techniques',
      'Building emotional resilience'
    ]
  },
  {
    id: 'team-building-activities',
    title: 'Team Building Activities',
    icon: Sparkles,
    color: 'from-indigo-500 to-violet-500',
    bgColor: 'bg-indigo-50',
    iconColor: 'text-indigo-600',
    description: 'Experiential, interactive sessions designed to reinforce teamwork concepts in engaging ways.',
    keyAreas: [
      'Encourage collaboration and communication',
      'Break departmental silos',
      'Build trust and team bonding',
      'Improve problem-solving skills'
    ]
  },
  {
    id: 'motivational-sessions',
    title: 'Motivational Sessions',
    icon: Zap,
    color: 'from-yellow-500 to-amber-500',
    bgColor: 'bg-yellow-50',
    iconColor: 'text-yellow-600',
    description: 'Inspire employees to stay focused, positive, and goal-oriented for driving performance.',
    keyAreas: [
      'Align with organizational goals',
      'Build confidence and self-belief',
      'Overcome challenges and setbacks',
      'Develop a growth mindset'
    ]
  },
  {
    id: 'gamification',
    title: 'Gamification in Corporate Training',
    icon: Gamepad2,
    color: 'from-fuchsia-500 to-pink-500',
    bgColor: 'bg-fuchsia-50',
    iconColor: 'text-fuchsia-600',
    description: 'Uses game-based elements to make learning interactive and engaging.',
    keyAreas: [
      'Higher participation and knowledge retention',
      'Friendly competition and teamwork',
      'Faster skill development',
      'Increased learner engagement'
    ]
  },
  {
    id: 'energizers',
    title: 'Energizers',
    icon: Sparkles,
    color: 'from-orange-500 to-red-500',
    bgColor: 'bg-orange-50',
    iconColor: 'text-orange-600',
    description: 'Short, high-impact activities used during training sessions to boost energy and focus.',
    keyAreas: [
      'Break monotony',
      'Improve attention and engagement',
      'Create a positive learning environment',
      'Re-energize participants'
    ]
  },
  {
    id: 'yoga-meditation',
    title: 'Yoga & Meditation Programs',
    icon: Heart,
    color: 'from-emerald-500 to-teal-500',
    bgColor: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    description: 'Corporate wellness programs supporting physical health, mental clarity, and emotional balance.',
    keyAreas: [
      'Reduced stress and anxiety',
      'Improved focus and productivity',
      'Better posture and physical fitness',
      'Enhanced emotional resilience'
    ]
  }
];

export const CorporateServicesDetail = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const programsRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const isProgramsInView = useInView(programsRef, { once: true, margin: "-100px" });

  const industry = getIndustryBySlug('corporate-services');
  const Icon = industry?.icon || Presentation;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
    }
  };

  // SEO configuration
  const SITE_URL = 'https://www.isde.academy';
  const canonicalUrl = `${SITE_URL}/corporate-training-course-in-india`;

  // Breadcrumb structured data
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": SITE_URL
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Industry Training",
        "item": `${SITE_URL}/industry-training`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Corporate Training Courses in India",
        "item": canonicalUrl
      }
    ]
  };

  // Course structured data
  const courseStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Corporate Training Programs in India",
    "description": "Industry-focused corporate training programs covering leadership, technical skills, compliance & performance improvement.",
    "numberOfItems": corporateTrainingPrograms.length + (industry?.trainings?.length || 0),
    "itemListElement": corporateTrainingPrograms.map((program, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Course",
        "name": program.title,
        "description": program.description,
        "provider": {
          "@type": "Organization",
          "name": "ISDE Academy"
        }
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Corporate Training Programs in India | Professional Skill Training</title>
        <meta name="description" content="Industry-focused corporate training programs in India covering leadership, technical skills, compliance & performance improvement." />
        <meta name="keywords" content="corporate training india, corporate training courses, leadership training, team building, business english training, soft skills training, employee development" />

        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:title" content="Corporate Training Programs in India | Professional Skill Training" />
        <meta property="og:description" content="Industry-focused corporate training programs in India covering leadership, technical skills, compliance & performance improvement." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(courseStructuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-indigo-900 via-purple-800 to-indigo-900 text-white pt-40 pb-16 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/80 to-purple-900/80" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-indigo-200 mb-6">
              <button onClick={() => navigate('/industry-training')} className="hover:text-white transition-colors">
                Industry Training
              </button>
              <span>/</span>
              <span className="text-white">Corporate Training</span>
            </div>

            <div className="flex items-center gap-6 mb-6">
              <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <Icon className="h-10 w-10 text-white" />
              </div>
              <div>
                <h1 className="text-4xl lg:text-5xl font-bold mb-2">Corporate Training Courses in India</h1>
              </div>
            </div>

            <p className="text-lg text-indigo-100 max-w-4xl leading-relaxed mb-6">
              India's corporate landscape is evolving rapidly. With globalization, digital transformation, hybrid work models, and increasing competition, organizations are realizing that technical skills alone are no longer enough to drive success. Communication, leadership, collaboration, emotional intelligence, and employee well-being have become equally important.
            </p>
            <p className="text-lg text-indigo-100 max-w-4xl leading-relaxed">
              A comprehensive corporate training program in India covers a wide range of learning areas from Business English and Behavioral Training to Leadership Development, Team Building, Stress Management, Motivational Sessions, Gamification, Energizers, and wellness initiatives such as Yoga & Meditation.
            </p>
          </motion.div>
        </div>

        {/* Decorative wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* What is Corporate Training */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              What is Corporate Training Course?
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-4xl">
              Corporate training courses in India are structured learning and development initiatives designed specifically for working professionals within organizations. These programs focus on improving employee skills, workplace behavior, mindset, and overall effectiveness in alignment with business goals.
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                'Improve communication and interpersonal skills',
                'Develop leadership and people management capabilities',
                'Strengthen teamwork and collaboration',
                'Enhance motivation, engagement, and performance',
                'Reduce workplace stress and conflict',
                'Promote employee wellness and work-life balance'
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-start gap-3 p-4 rounded-lg bg-indigo-50 hover:bg-indigo-100 transition-colors"
                >
                  <CheckCircle className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Importance of Corporate Training */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Importance of Corporate Training Courses in India
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: 'Enhancing Employee Performance',
                description: 'Corporate training equips employees with practical skills and the right mindset to perform effectively in fast-paced work environments.',
                icon: TrendingUp,
                color: 'bg-blue-500'
              },
              {
                title: 'Developing Strong Leaders',
                description: 'Leadership development programs help Indian organizations build a strong pipeline of future-ready managers and leaders.',
                icon: Target,
                color: 'bg-purple-500'
              },
              {
                title: 'Improving Workplace Culture',
                description: 'Behavioral and soft-skills training fosters professionalism, respect, and collaboration across teams.',
                icon: Users,
                color: 'bg-green-500'
              },
              {
                title: 'Increasing Employee Engagement',
                description: 'Motivational sessions, gamification, and energizers boost morale, participation, and enthusiasm at work.',
                icon: Sparkles,
                color: 'bg-amber-500'
              },
              {
                title: 'Supporting Employee Well-Being',
                description: 'Stress management, yoga, and meditation programs address burnout and promote mental and physical health.',
                icon: Heart,
                color: 'bg-rose-500'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow border-0 bg-white">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center mb-4`}>
                      <item.icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Corporate Training Courses */}
      <section ref={programsRef} className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isProgramsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Key Corporate Training Courses in India
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive training programs designed to build confident professionals, strong leaders, and a healthy workplace culture
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isProgramsInView ? "visible" : "hidden"}
            className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          >
            {corporateTrainingPrograms.map((program) => {
              const ProgramIcon = program.icon;
              return (
                <motion.div
                  key={program.id}
                  variants={cardVariants}
                >
                  <Card className="group h-full hover:shadow-xl transition-all duration-300 border-0 bg-white overflow-hidden">
                    {/* Gradient Header */}
                    <div className={`h-3 bg-gradient-to-r ${program.color}`} />

                    <CardHeader className="pb-2">
                      <div className={`w-14 h-14 ${program.bgColor} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                        <ProgramIcon className={`h-7 w-7 ${program.iconColor}`} />
                      </div>
                      <CardTitle className="text-lg font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">
                        {program.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="pt-0">
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {program.description}
                      </p>

                      <div className="space-y-2">
                        {program.keyAreas.slice(0, 3).map((area, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm">
                            <CheckCircle className={`h-4 w-4 ${program.iconColor} flex-shrink-0 mt-0.5`} />
                            <span className="text-gray-600 line-clamp-1">{area}</span>
                          </div>
                        ))}
                        {program.keyAreas.length > 3 && (
                          <p className="text-xs text-gray-400 pl-6">
                            +{program.keyAreas.length - 3} more areas
                          </p>
                        )}
                      </div>

                      <Button
                        variant="outline"
                        className="w-full mt-4 border-gray-200 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-600"
                        onClick={() => navigate('/contact')}
                      >
                        Enquire Now
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Benefits of Corporate Training Courses in India
            </h3>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {[
              'Improved employee productivity and performance',
              'Stronger leadership and management capabilities',
              'Better communication and teamwork',
              'Reduced workplace conflict and stress',
              'Higher employee engagement and retention',
              'Positive and inclusive organizational culture'
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="h-4 w-4 text-white" />
                </div>
                <span className="text-gray-700 font-medium">{benefit}</span>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center text-gray-600 mt-10 max-w-4xl mx-auto"
          >
            Corporate training courses in India are a strategic investment in people, performance, and long-term organizational success. In a competitive and evolving Indian business environment, companies that prioritize employee development and well-being gain a sustainable edge.
          </motion.p>
        </div>
      </section>

      {/* Compliance & Governance Training Programs (Existing) */}
      {industry && industry.trainings && industry.trainings.length > 0 && (
        <section ref={ref} className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Compliance & Governance <span className="text-indigo-600">Training Programs</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Specialized training in ESG, sustainability, risk management, and corporate governance for modern businesses
              </p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {industry.trainings.map((training) => {
                const TrainingIcon = training.icon;
                return (
                  <motion.div
                    key={training.id}
                    variants={cardVariants}
                  >
                    <Card
                      className="group h-full hover:shadow-2xl transition-all duration-300 border-0 bg-gradient-to-br from-white to-indigo-50/30 cursor-pointer overflow-hidden relative"
                      onClick={() => navigate(training.customPath || `/industry-training/corporate-services/${training.slug}`)}
                    >
                      {/* Decorative gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

                      {/* Header with gradient */}
                      <div className="relative h-24 bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center">
                        <TrainingIcon className="h-12 w-12 text-white" />
                        {training.certificationLevels && (
                          <Badge className="absolute top-3 right-3 bg-yellow-400 text-gray-900 border-0">
                            {training.certificationLevels.length} Levels
                          </Badge>
                        )}
                      </div>

                      <CardHeader className="pb-3 relative z-10">
                        <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors leading-tight line-clamp-2 mb-3">
                          {training.title}
                        </CardTitle>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {training.shortDescription}
                        </p>
                      </CardHeader>

                      <CardContent className="space-y-4 relative z-10">
                        {/* Training details */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <Clock className="h-4 w-4 text-indigo-600" />
                            <span className="font-medium">Duration:</span>
                            <span>{training.duration}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <Award className="h-4 w-4 text-indigo-600" />
                            <span className="font-medium">Level:</span>
                            <span>{training.level}</span>
                          </div>
                          {training.deliveryMode && (
                            <div className="flex items-center gap-2 text-sm text-gray-700 flex-wrap">
                              <span className="font-medium">Mode:</span>
                              {training.deliveryMode.map((mode, idx) => (
                                <Badge
                                  key={idx}
                                  variant="outline"
                                  className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200"
                                >
                                  {mode}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* CTA */}
                        <div className="pt-4 border-t border-gray-100">
                          <Button
                            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(training.customPath || `/industry-training/corporate-services/${training.slug}`);
                            }}
                          >
                            View Details
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>
      )}

      {/* Upcoming Trainings */}
      <UpcomingTrainingsSection
        pageSlug="corporate-services"
        title="Upcoming Corporate Training Sessions"
      />

      {/* Related Courses */}
      <RelatedCoursesSection currentSlug="iso-9001" slugs={['iso-9001', 'iso-45001', 'iatf-16949']} heading="Related ISO Training Courses" />

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-3xl p-12 text-center text-white"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Ready to Transform Your Workforce?
            </h2>
            <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
              Corporate training is no longer optional - it is essential for growth, resilience, and excellence. Join thousands of organizations investing in their people.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                size="lg"
                className="bg-white text-indigo-600 hover:bg-indigo-50 px-8 py-6 text-lg"
                onClick={() => navigate('/training-calendar')}
              >
                View Training Calendar
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                onClick={() => navigate('/contact')}
              >
                Request Custom Training
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};
